<template>
    <div class="h-full p-4">
        <ai-grid-edit-wrapper
            ref="gridRef"
            :meta="meta"
            :to-remove-toolbar-items-names="toRemoveToolbarItemsNames"
            :toolbar-user-items="toolbarUserItems"
            @grid-edit-wrapper-mounted="onGridEditWrapperMounted"
            @toolbar-button-click="onToolbarButtonClick"
        >
            <template #customElement>
                <custom-filter
                    ref="customFilterRef"
                    :data-source="statusFilterOptions"
                    @update-filters="onFiltersUpdate"
                />
            </template>
            <template #userStatusIcon="{ data }">
                <div class="flex justify-center align-center">
                    <img width="15px" :src="getStatusIcon(data.data.status)" />
                </div>
            </template>
        </ai-grid-edit-wrapper>
        <component :is="usersOfficialPopupSwitcher" />
        <component
            ref="usersPopupRef"
            :is="usersPopupSwitcher"
            @save-click="refreshUserPopupData"
            @refresh-user-grid="refreshUserGrid"
        />
        <component :is="userSubdivisionPopupSwitcher" />
        <component :is="allRolesPopupSwitcher" :style="displayAllRolesPopup" @items-event="receiveItems" />
        <component :is="roleAreasPopupSwitcher" />
        <component :is="massLocksPopupSwitcher" :users="selectedUsers" @refresh-user-grid="refreshUserGrid" />
        <component :is="userLocksPopupSwitcher" @refresh-user-grid="refreshUserGrid" />
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';

import AiGridEditWrapper from '@/components/wrappers/ai-grid-edit-wrapper.vue';
import UsersOfficialPopup from '@/views/administration/users/users-official-popup.vue';
import UsersPopup from '@/views/administration/users/users-popup.vue';
import UsersSubdivisionPopup from '@/views/administration/users/users-subdivision-popup.vue';
import AllRolesPopup from '@/views/administration/users/all-roles-popup.vue';
import RoleAreasPopup from '@/views/administration/users/role-areas-popup.vue';
import MassLocksPopup from '@/views/administration/users/mass-locks-popup.vue';
import UserLocksPopup from '@/views/administration/users/user-locks-popup.vue';
import CustomFilter from '@/views/administration/users/custom-filter-form.vue';

import environmentStore from '@/store/enviroment-data.js';
import usersLocalStore from '@/views/administration/users/store.js';
import usersStore from '@/store/administration/users.js';
import rolesStore from '@/store/administration/roles.js';
import { setActiveViewInfo, setViewData } from '@/layouts/main-layout/main-layout-data.js';
import { dxUiNotify } from '@/utils/dx-helper.js';
import { deleteUsers as deleteUsersRequest } from '@/service/admin-service.js';

import activeUserImg from '../../../../public/assets/active-user.svg';
import lockedUserImg from '../../../../public/assets/locked-user.svg';
import deletedUserImg from '../../../../public/assets/deleted-user.svg';
import { svgAiLibrary } from '@/service/svg-ai-library';

const { state: environmentState, getAppList, disableUiElement } = environmentStore;
const { state, MODE, UI_ELEMENTS_IDS, USER_STATUS, clearUserRequestBody, setUserRequestBody, clearLockFormData } = usersLocalStore;
const {
    usersState,
    setUserIds,
    setOfficialUserIds,
    fetchUserData,
    fetchOfficialUserData,
} = usersStore;
const { rolesState, getRoleList } = rolesStore;

const usersOfficialPopupSwitcher = computed(() => state.officialPopupVisible ? UsersOfficialPopup : null);
const usersPopupSwitcher = computed(() => state.userCardPopupVisible ? UsersPopup : null);
const userSubdivisionPopupSwitcher = computed(() => state.subdivisionPopupVisible ? UsersSubdivisionPopup : null);
const allRolesPopupSwitcher = computed(() => state.usersRolePopupVisible ? AllRolesPopup : null);
const roleAreasPopupSwitcher = computed(() => state.roleAreasPopupVisible ? RoleAreasPopup : null);
const massLocksPopupSwitcher = computed(() => state.massLocksPopupVisible ? MassLocksPopup : null);
const userLocksPopupSwitcher = computed(() => state.userLocksPopupVisible ? UserLocksPopup : null);
const displayAllRolesPopup = computed(() => state.roleAreasPopupVisible ? { display: 'none' } : {});

const gridRef = ref();
const usersPopupRef = ref();
const toRemoveToolbarItemsNames = ['add', 'copy', 'save', 'choice', 'edit', 'delete'];
const selectedUsers = ref();
const customFilterRef = ref();
const isDisableUiElement = disableUiElement(UI_ELEMENTS_IDS.USERS_GRID);
let gridCoreRef;
let gridCoreInstance;
let filterValue = [USER_STATUS.ACTIVE];

const statusFilterOptions = [
    {
        dataField: USER_STATUS.ACTIVE,
        label: 'Показывать активных',
        shortName: 'активных',
        value: true,
    },
    {
        dataField: USER_STATUS.DELETED,
        label: 'Показывать архивированных',
        shortName: 'архивированных',
        value: false,
    },
    {
        dataField: USER_STATUS.BLOCKED,
        label: 'Показывать заблокированных',
        shortName: 'заблокированных',
        value: false,
    },
];

const onGridEditWrapperMounted = async () => {
    gridCoreRef = gridRef.value.ref.gridRef;
    gridCoreInstance = gridRef.value.ref.gridRef.gridInstance;
    gridRef.value.ref.gridRef.gridInstance.on('selectionChanged', onSelectionChanged);
    setInstance();
    disableToolbarElements();
    await setViewData();
    onFiltersUpdate([USER_STATUS.ACTIVE]);
};

const setInstance = () => {
    const instance = {
        refresh: gridRef.value.ref.setDataSource,
        userData: () => {},
        userSettings: () => {},
    };
    setActiveViewInfo(instance);
}

const disableToolbarElements = () => {
    gridCoreRef.setRefValue('isAddUserDropDownButtonDisabled', isDisableUiElement);
    updateButtonState('isMassLockButtonDisabled');
    updateButtonState('isUserDeleteButtonDisabled');
}

const onSelectionChanged = () => {
    updateButtonState('isMassLockButtonDisabled');
    updateButtonState('isUserDeleteButtonDisabled');
}

const updateButtonState = (name) => {
    const selectedRowsData = gridCoreInstance.getSelectedRowsData();
    const isButtonDisabled = isDisableUiElement|| !selectedRowsData.length;
    gridCoreRef.setRefValue(name, isButtonDisabled);
}

const onFiltersUpdate = selectedFilters => {
    statusFilterOptions.forEach((option) => option.value = selectedFilters?.includes(option.dataField));
    filterValue.length = 0;
    filterValue.push(...selectedFilters);
    const newFilterValue = filterValue.length ? ['status', 'anyof', filterValue] : [];
    gridCoreInstance.option('filterValue', newFilterValue);
};

const receiveItems = selectedItems => {
    const newRoles = selectedItems.filter(role => !state.userRequestBody?.roles?.some(currentRole => currentRole.id === role.id));
    const roles = state.userRequestBody.roles ? [...state.userRequestBody.roles, ...newRoles] : [...newRoles];
    state.userRequestBody.roles = [...roles];
};

const onToolbarButtonClick = event => {
    switch (event.item.component.name) {
        case 'user-delete':
            deleteUsersByCheckbox();
            break;
        case 'mass-lock':
            onMassLockClick();
            break;
        case 'add-user':
            onAddNewUser();
            break;
    }
};

const onMassLockClick = () => {
    selectedUsers.value = gridCoreInstance.getSelectedRowsData();
    state.massLocksPopupVisible = true;
};

const showUsersPopup = async event => {
    await getAppList();
    await refreshUserPopupData(event);
    state.userCardPopupVisible = true;
};

const refreshUserPopupData = async (event) => {
    stopWatchingAndInitConfirmedState();
    const userData = event.row.key;
    setUserIds(userData);
    await getRoleList();
    const user = await mergeUserData();
    state.mode = MODE.UPDATE;
    clearUserRequestBody();
    state.deletedRoleKey = [];
    setUserRequestBody({ ...user, id: userData });
    state.userRequestBody.lock_reason = state.userRequestBody.lock_reason
        ? Array.isArray(state.userRequestBody.lock_reason)
            ? state.userRequestBody.lock_reason.map(reason => ({ id: new Date().getTime(), name: reason }))
            : [{ id: new Date().getTime(), name: state.userRequestBody.lock_reason }]
        : [];
    concatRolesAppIdWithName();
    if (state.userCardPopupVisible) usersPopupRef.value.startWatching();

};

const concatRolesAppIdWithName = () => {
    const getAppsNamesString = (roleApps) => {
        return roleApps
            .map(appId => environmentState.appList.find(environmentApp => environmentApp.kod_app === appId)?.name_app)
            .join(', ');
    };

    state.userRequestBody.roles = rolesState.roleList
        .filter(role => state.userRequestBody.roles.includes(role.id))
        .map(role => ({
            ...role,
            apps_names: getAppsNamesString(role.apps),
        }));
};


const refreshUserGrid = async () => {
    await gridRef.value.ref.setDataSource();
};

const stopWatchingAndInitConfirmedState = () => {
    state.stopWatching && state.stopWatching();
    state.isClosingConfirmed = true;
    state.dataHasNotChanged = true;
    state.requestBodyHasNotChanged = true;
    state.userBlockagesHasNotChanged = true;
};

const mergeUserData = async () => {
    const userData = await fetchUserData();
    setOfficialUserIds(usersState.userData.official_id);
    const officialUserData = await fetchOfficialUserData(userData.official_id);
    const user = { ...officialUserData, ...userData };
    return user;
};

const showUserLocks = (event) => {
    state.userRequestBody.id = event.row.data.id;
    state.userRequestBody.fio = event.row.data.fio;
    state.userRequestBody.locks = [];
    state.userLocksPopupVisible = true;
};

const onAddNewUser = async () => {
    stopWatchingAndInitConfirmedState();
    clearUserRequestBody();
    clearLockFormData();
    state.mode = MODE.CREATE;
    await getAppList();
    await getRoleList(null);
    state.currentTabIndex = 0;
    state.officialPopupVisible = true;
};

const deleteUsersByCheckbox = async () => {
    const rowsToDelete = [];
    const selectedRowKeys = gridCoreInstance.getSelectedRowKeys();
    rowsToDelete.push(...selectedRowKeys);
    await deleteUsers(rowsToDelete);
}

const deleteUserByContextMenu = async (userId) => {
    const rowsToDelete = [userId];
    await deleteUsers(rowsToDelete);
}

const deleteUsers = async (rowsToDelete) => {
    const response = await deleteUsersRequest(rowsToDelete);
    if (response?.result && response?.success) {
        dxUiNotify('Пользователи успешно удалены', 'success');
    } else if (response?.result) {
        dxUiNotify(`Успешно удалено ${response.resume.successCount} пользователей, ${response.resume.userFailedCount} пользователей не подошло под условия удаления`, 'warning');
        if (response.resume.successCount === 0) {
            return;
        }
    } else {
        dxUiNotify(`Ошибка при удалении пользователей: ${response?.errors[0]?.message}`, 'error');
        return;
    }
    await refreshUserGrid();
};

const getStatusIcon = (status) => {
    switch (status) {
        case USER_STATUS.ACTIVE:
            return activeUserImg;
        case USER_STATUS.DELETED:
            return deletedUserImg;
        case USER_STATUS.BLOCKED:
            return lockedUserImg;
    }
};

const toolbarUserItems = [
    {
        widget: 'dx-drop-down-button',
        index: 1,
        component: {
            htmlElement: svgAiLibrary['ai-add-base'],
            name: 'export-excel-all',
            text: 'Экспортировать всё в EXCEL',
            disabledRefName: 'isAddUserDropDownButtonDisabled',
            dropDownOptions: { width: 350 },
            items: [
                {
                    widget: 'dx-drop-down-item',
                    component: {
                        name: 'add-user',
                        text: 'Создать пользователя',
                    },
                },
                {
                    widget: 'dx-drop-down-item',
                    component: {
                        name: 'add-user-from-active-directory',
                        text: 'Создать пользователя из ActiveDirectory',
                    },
                },
            ],
        },
    },
    {
        widget: 'dx-button',
        index: 2,
        component: {
            name: 'user-delete',
            disabledRefName: 'isUserDeleteButtonDisabled',
            hint: 'Удалить',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-delete-base',
        },
    },
    {
        widget: 'dx-button',
        index: 3,
        component: {
            name: 'mass-lock',
            disabledRefName: 'isMassLockButtonDisabled',
            hint: 'Массовая блокировка',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-mass-lock',
        },
    },
];

const meta = {
    keyExpr: 'id',
    customElement: true,
    wordWrapEnabled: true,
    scrollingMode: 'standart',
    focusedRowEnabled: true,
    allowSelectAll: true,
    headerFilterVisible: true,
    updateSearchPanelOrFilterRowValueTimeout: 1000,
    api: {
        select: 'admin./user/list/get',
    },
    rights: {
        INS: false,
        UPD: false,
        DEL:  false,
    },
    contextMenuItems: [
        {
            text: 'Открыть карточку пользователя',
            onItemClick: showUsersPopup,
        },
        {
            text: 'Блокировки пользователя',
            onItemClick: showUserLocks,
        },
        {
            text: 'Удалить',
            disabled: isDisableUiElement,
            onItemClick: (e) => deleteUserByContextMenu(e.row.key),
        },
    ],
    columns: [
        {
            dataField: 'id',
            caption: 'ID',
            visible: false,
            dataType: 'number',
            allowEditing: false,
        },
        {
            dataField: 'user_login',
            caption: 'Логин',
            dataType: 'string',
            allowEditing: false,
        },
        {
            dataField: 'fio',
            caption: 'ФИО',
            sortOrder: 'asc',
            dataType: 'string',
            allowEditing: false,
        },
        {
            dataField: 'org_name',
            caption: 'Принадлежность к филиалу/организации',
            dataType: 'string',
            allowEditing: false,
        },
        {
            dataField: 'dep_name',
            caption: 'Подразделение',
            dataType: 'string',
            allowEditing: false,
        },
        {
            dataField: 'name_fun',
            caption: 'Должность',
            dataType: 'string',
            allowEditing: false,
        },
        {
            dataField: 'e_mail',
            caption: 'Электронная почта',
            dataType: 'string',
            allowEditing: false,
        },
        {
            dataField: 'phone',
            caption: 'Рабочий телефон',
            dataType: 'string',
            allowEditing: false,
        },
        {
            dataField: 'status',
            caption: 'Статус',
            cellTemplate: 'userStatusIcon',
        },
        {
            dataField: 'roles',
            caption: 'Роли пользователя',
            dataType: 'string',
            allowEditing: false,
        },
    ],
};
</script>
